import type { ReactNode } from 'react';
import type { StaffState } from './types';

/**
 * The three ways FacilityWISE says "nothing here", which are NOT
 * interchangeable. Confusing them is the defect this module exists to avoid.
 *
 *   dash()      an EMPTY field on a present record — grey em-dash
 *   NotLoaded   a MISSING source record — an explicit panel, nothing else
 *   bit()       an UNANSWERED question — italic grey ·, counted separately
 */

/** An empty field on a record that exists. */
export function dash(v: ReactNode): ReactNode {
  if (v === null || v === undefined || v === '' || v === 'None') {
    return <span className="na">—</span>;
  }
  return v;
}

/**
 * A facility with no row in one of the source tables.
 *
 * Renders the gap and nothing else — never a default. On a system of record a
 * plausible-looking wrong value (Pacific time zone on a Maryland site, 8.25%
 * tax in a 6% jurisdiction) is worse than a visible hole, because someone will
 * act on it. This is non-negotiable per the design handoff.
 */
export function NotLoaded({ what }: { what: string }) {
  return (
    <div className="unloaded">
      <b>No {what} record</b>
      <span>
        This facility has no row in the source list. Nothing is shown rather than a default that
        would read as fact.
      </span>
    </div>
  );
}

export interface Bit {
  k: string;
  cls: 'yes' | 'no' | 'unk';
  t: string;
  /**
   * The magnitude, when the value arrived as a NUMBER — so a caller can show
   * how many, not just whether. null for a bit, for free text and for an
   * unanswered question; 0 is a recorded count of none and is NOT null.
   */
  n: number | null;
}

/**
 * Three-state feature bit.
 *
 * null and 0 mean different things and must never be coalesced: 0 is an
 * answered No, null is a question nobody answered. They render differently and
 * the panel header counts them separately ("3 of 9 · 2 unanswered").
 *
 * ⚠️ A NUMBER IS THREE-STATE BY MAGNITUDE, NEVER BY EQUALITY WITH 1.
 * This used to read `v === true || v === 1 || v === '1'`, which asks "is this
 * exactly one?" and calls every other number an answered No. Some feature
 * columns in Facility_Features are declared int and carry the COUNT in the flag
 * column itself: `Moving_Truck` is the demonstrated instance — the Power App
 * renders it as a numeric field, and there is no `Moving_Truck_Quantity` among
 * the source columns, so the number has nowhere else to live. Under the old
 * test a site with two moving trucks rendered an answered "No", identical to a
 * site with none, and the magnitude was destroyed on the way to the screen.
 *
 * The rule now: n > 0 is Yes, n === 0 is an answered No, null/undefined is
 * still unanswered. Booleans and the string '1' behave exactly as before, and
 * the magnitude comes back as `n` — this function does not decide how (or
 * whether) it is displayed.
 */
export function bit(v: boolean | number | string | null | undefined): Bit {
  if (v === null || v === undefined) return { k: '·', cls: 'unk', t: 'Not answered', n: null };
  if (typeof v === 'number') {
    return v > 0
      ? { k: '✓', cls: 'yes', t: 'Yes', n: v }
      : { k: '○', cls: 'no', t: 'No', n: v };
  }
  const yes = v === true || v === '1';
  return yes
    ? { k: '✓', cls: 'yes', t: 'Yes', n: null }
    : { k: '○', cls: 'no', t: 'No', n: null };
}

/** Brand accent. Encoded as colour, so it needs the key affordances in the UI. */
export const BRAND_C: Record<string, string> = {
  'US Storage Centers': '#0365d9',
  'AC Self Storage': '#b8860b',
  'Everest Self Storage': '#6b48e8',
  'SafeNest Self Storage': '#1f8a5b',
  'Small Town Storage': '#c2410c',
};

export const brandColor = (b: string | null): string => (b && BRAND_C[b]) || '#7b8da4';

/** Short brand label for the toolbar swatch key. */
export const shortBrand = (b: string): string => b.replace(/ Self Storage| Storage Centers/, '');

export function initials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('');
}

/** `Now Hiring` is a vacancy sentinel in the source, not a person's name. */
export const isVacancy = (name: string | null): boolean => name === 'Now Hiring';

/**
 * `None` in `Facility_Staff` is the DM recording that the position does not
 * exist at this facility (the Power App offers it for every role but the PM).
 */
export const isNoPosition = (name: string | null): boolean => name === 'None';

/**
 * Why the on-site team is not shown, in one wording for every screen. `absent`
 * is a fact about the source; the two `not_loaded` reasons are facts about this
 * read, and neither may ever be printed as "no record".
 */
export function teamUnavailable(team: Exclude<StaffState, { state: 'present' }>): string {
  return team.state === 'absent'
    ? 'No Facility Staff record'
    : team.reason === 'ambiguous_source'
      ? 'Conflicting Facility Staff records'
      : 'Facility Staff could not be read';
}

/** A real person, as opposed to a vacancy, a sentinel, or an empty cell. */
export const isPerson = (name: string | null): boolean =>
  !!name && name !== 'None' && name !== 'Now Hiring';

export const money = (n: number | null): string | null =>
  n === null ? null : `$${n.toLocaleString()}`;

export const pct = (n: number | null): string | null => (n === null ? null : `${n}%`);
